import { Container, Divider } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { Box, Grid } from "@mui/material";
import axios from "axios";
import {
  AutoCompleteWithSearch,
  BalanceCard,
  CustomTable,
  ModalError,
  Page,
  PrimaryButton,
  SearchTextInput,
  SelectInput,
  YearPicker,
} from "components";
import { ButtonGroupTop } from "components/atoms/Button";
import { HeaderTitle } from "layouts";
import { debounce } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors, wordCapitalize } from "utils";
import { ModalCostAllocationSummary } from "./Components/ModalCostAllocationSummary";
import ModalDefaultTresshold from "./Components/ModalDefaultTresshold";
import ModalFormCostAllocation from "./Components/ModalFormCostAllocation";
import {
  columnTable,
  optionsStatusAdvanceComposer,
  summaryAttributes,
} from "./Constants";

function CostAllocation() {
  const classes = globalStyles();
  const history = useHistory();
  const location = useLocation();
  const isFirstLoad = useRef(true);
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const urlParams = new URLSearchParams(location.search);
  const paramsSearch = urlParams?.get("search") || "";
  const paramsPage = urlParams?.get("page") || 1;
  const paramsSize = urlParams?.get("size") || 10;
  const paramsStatus = urlParams?.get("status") || "";
  const paramsYear = urlParams?.get("year");
  const paramsComposer = urlParams?.get("composer_id");

  if (urlParams.has("per_page")) {
    urlParams.delete("per_page");
  }

  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: Number(paramsPage) || 1,
    size: Number(paramsSize) || 10,
    search: paramsSearch || "",
    status: paramsStatus || "",
    sort: "",
    year: Number(paramsYear) || moment().year(),
    composer_id: Number(paramsComposer) || "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [dataSummary, setDataSummary] = useState([]);
  const [optionComposer, setOptionComposer] = useState([]);
  const [modalCreateVisible, setModalCreateVisible] = useState(false);
  const [modalDefaultVisible, setModalDefaultVisible] = useState(false);
  const [modalSummaryVisible, setModalSummaryVisible] = useState(false);
  const [modalSummaryTitle, setModalSummaryTitle] = useState("");
  const [modalSummaryFilter, setModalSummaryFilter] = useState("");
  const handleChangePageParams = (value, key) => {
    if (value) {
      urlParams.set(key, value);
    } else {
      urlParams.delete(key);
    }
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };

  const handleChangeQueryParams = (value, key) => {
    console.log({ value, key });
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));

    handleChangePageParams(value, key);
    if (key !== "page") {
      handleChangePageParams(1, "page");
    }
  };

  const getDataSummary = async () => {
    const { year, composer_id } = queryParams;
    try {
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction/v2/cost-allocation/summary`,
        {
          headers,
          params: {
            year,
            ...(composer_id && { composer_ids: composer_id }),
          },
        }
      );
      const { data } = res.data;
      const categories = ["total", "settled", "unsettled"];
      const modifiedData = categories.map(category => {
        return {
          card_name: `${wordCapitalize(
            category === "settled"
              ? "Settled"
              : category === "unsettled"
              ? "Unsettled"
              : category
          )}`,
          total_amount: data?.[category]?.total_amount || 0,
          total_document: data?.[category]?.document_count || 0,
          type: category,
        };
      });

      setDataSummary(modifiedData);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };

  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction/v2/cost-allocation`,
        {
          headers,
          params: {
            ...Object.fromEntries(
              Object.entries(queryParams).filter(
                ([item, v]) =>
                  v !== "" &&
                  v !== null &&
                  v !== undefined &&
                  item !== "composer_id"
              )
            ),
            ...(queryParams.composer_id && {
              composer_ids: queryParams.composer_id,
            }),
          },
        }
      );
      const { data, meta, total_page } = res?.data;
      setDataTable(data);
      const calculatedPages =
        meta?.total && meta?.limit ? Math.ceil(meta.total / meta.limit) : 1;
      setTableTotalPage(total_page || meta?.last_page || calculatedPages);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );

  const getComposerList = async () => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction/v2/cost-allocation/composers`,
        { headers }
      );
      const modifiedData = (res?.data?.data || [])
        ?.filter(({ composer_name }) => composer_name)
        ?.map(item => ({
          label: item.composer_name,
          id: item.composer_id,
        }));
      const uniqueItems = modifiedData.filter(
        (item, index, array) =>
          index === array.findIndex(el => el.label === item.label)
      );
      setOptionComposer(uniqueItems);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };

  const fetchData = async () => {
    setLoadingPage(true);
    const promises = [getDataSummary(), getDataTable(), getComposerList()];

    try {
      await Promise.all(promises);
    } catch (error) {
      ModalError(error, "Error fetching data");
    } finally {
      setLoadingPage(false);
      isFirstLoad.current = false;
    }
  };

  useEffect(() => {
    // Cleanup empty parameters from URL on load
    const keysToCleanup = ["search", "status", "composer_id"];
    let hasChanges = false;
    keysToCleanup.forEach(key => {
      if (urlParams.get(key) === "") {
        urlParams.delete(key);
        hasChanges = true;
      }
    });

    if (hasChanges) {
      history.replace({
        pathname: location.pathname,
        search: urlParams.toString(),
      });
    }
  }, []);

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      fetchData();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Page className={classes.root} title="Cost Allocation">
      <Container maxWidth={false}>
        <HeaderTitle title="Cost Allocation" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes?.flatDivider} />
        <Grid container columnGap={2} my="16px">
          <Grid item>
            <AutoCompleteWithSearch
              options={optionComposer}
              placeholder="All Composer"
              value={
                optionComposer.find(
                  option =>
                    String(option.id) === String(queryParams.composer_id)
                ) || null
              }
              isOptionEqualToValue={(option, value) =>
                String(option?.id) === String(value?.id || value)
              }
              onChange={value => {
                handleChangeQueryParams(value?.id, "composer_id");
              }}
              onInputChange={() => {}}
              optionLabel="label"
              uniqueKey="id"
              label="Composer"
              width={300}
            />
          </Grid>

          <Grid item>
            <YearPicker
              label="Year"
              onChange={year => handleChangeQueryParams(year, "year")}
              value={queryParams?.year}
              width={200}
            />
          </Grid>
        </Grid>
        <Box display="flex" overflow="auto" columnGap={2} mt="16px">
          {dataSummary?.map((item, index) => {
            const isUnsettled = item.type?.toLowerCase() === "unsettled";
            return (
              <Box flexShrink={0} key={index}>
                <BalanceCard
                  title={item?.card_name}
                  value={item?.total_amount}
                  infoButton
                  tooltipTitle={`Cost Allocation Status ${
                    summaryAttributes?.find(
                      attribute => attribute?.tooltip_title === item?.card_name
                    )?.tooltip_title
                  }`}
                  onClick={() => {
                    setModalSummaryVisible(true);
                    setModalSummaryTitle(item?.card_name);
                    setModalSummaryFilter(item?.type);
                  }}
                  subTitle={`${item?.total_document} Docs`}
                  loading={loadingPage}
                  minWidth={350}
                  backgroundColor={isUnsettled && "#712D20"}
                />
              </Box>
            );
          })}
        </Box>
        <Grid
          container
          alignItems="flex-end"
          justifyContent="space-between"
          my={2}
        >
          <Grid item display="flex" xs={12} md={5}>
            <Box sx={{ width: 400 }}>
              <SearchTextInput
                width={350}
                placeholder="Search"
                value={queryParams?.search}
                onChange={e =>
                  handleChangeQueryParams(e?.target?.value, "search")
                }
              />
            </Box>

            <Box sx={{ width: 300 }}>
              <SelectInput
                width={250}
                label="Status"
                placeholder="None"
                value={queryParams?.status}
                onChange={e =>
                  handleChangeQueryParams(e?.target?.value, "status")
                }
                options={optionsStatusAdvanceComposer}
                optionKey="value"
                optionLabel="name"
              />
            </Box>
          </Grid>

          {/* Sisi Kanan: Action Button */}
          <Grid item xs={12} md={4} display="flex" justifyContent="flex-end">
            <PrimaryButton
              label="Create Cost Allocation"
              startIcon={<AddIcon />}
              onClick={() => setModalCreateVisible(true)}
              size="large"
            />
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.size}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "size")
          }
          isHaveAction
          handleView={item =>
            history.push(
              `/admin/detail-cost-allocation/${item?.cost_allocation_id}`
            )
          }
        />
      </Container>
      {modalCreateVisible && (
        <ModalFormCostAllocation
          open={modalCreateVisible}
          onClose={() => setModalCreateVisible(false)}
          optionComposer={optionComposer}
          getDataTable={getDataTable}
        />
      )}
      {modalDefaultVisible && (
        <ModalDefaultTresshold
          open={modalDefaultVisible}
          onClose={() => setModalDefaultVisible(false)}
          getDataTable={getDataTable}
        />
      )}
      {modalSummaryVisible && (
        <ModalCostAllocationSummary
          open={modalSummaryVisible}
          onClose={() => setModalSummaryVisible(false)}
          title={modalSummaryTitle}
          filter={modalSummaryFilter}
          params={queryParams}
        />
      )}
    </Page>
  );
}

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Transaction",
    link: "/admin/cost-allocation",
  },
  {
    label: "Cost Allocation",
    active: true,
  },
];
export default CostAllocation;
